
import { QuestionAnswers } from "../modules/Experiences/Checkout/BookingQuestions/BookingQuestionsUtils";
import { Order, OrderBody, OrderResponse } from "./booking-types";


export const getOrder = async (): Promise<Order> => {
  try {
    const response = await fetch(
      process.env.REACT_APP_API_URL + "/bookings/experience/" + localStorage.getItem("tp_order_id")
    );

    if (response.status === 404) {
      throw new Error("Order not found");
    }
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response text:", errorText);
      throw new Error("Network response was not ok");
    }
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return response.json();
    } else {
      const errorText = await response.text();
      console.error("Error: Expected JSON but received:", errorText);
      throw new Error("Received non-JSON response");
    }
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export interface FindBookingWithPaymentIdResponse {
  booking: {
    public_id: string;
    status: string;
    order_id: string;
    selected_date: string | null;
    selected_option: string | null;
    quotation: {
      title: string;
      has_available_time_spots: boolean;
    }
  };
  payment: {
    public_id: string;
    status: string;
    is_reservation: boolean;
    should_show_wire_transfer_data: boolean;
    should_show_bank_transfer_data: boolean;
    should_show_cash_transfer_data: boolean;
    transaction_pending_amount: number;
  };
}


export const getOrderFromPaymentId = async (paymentId: string): Promise<FindBookingWithPaymentIdResponse> => {
  try {
    const response = await fetch(
      process.env.REACT_APP_API_URL + "/bookings/payment/" + paymentId
    );

    if (response.status === 404) {
      throw new Error("Order not found");
    }
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response text:", errorText);
      throw new Error("Network response was not ok");
    }
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return response.json();
    } else {
      const errorText = await response.text();
      console.error("Error: Expected JSON but received:", errorText);
      throw new Error("Received non-JSON response");
    }
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
}

  export const postOrder = async (
  quotation_id: string,
  selectedDate: string,
  selectedOption: string,
  productTotalAmount: number,
  chargeable_travellers: number
): Promise<void> => {
  const body: OrderBody = {
    quotation_id: quotation_id,
    selected_date: selectedDate,
    selected_option: selectedOption,
    product_total_amount: productTotalAmount,
    chargeable_travellers: chargeable_travellers,
  }

  const tp_order_id = window.localStorage.getItem("tp_order_id")
  if (tp_order_id) {
    body.tp_order_id = tp_order_id;
  }

  try {
    const response = await fetch(
      process.env.REACT_APP_API_URL + "/bookings/create/experience",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      }
    );

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {

      const responseJson = await response.json() as OrderResponse;
      if (!response.ok) {
        // we're returning feedback to the user, as the only possible error is that the quotation is out of stock
        console.error("Error response message:", responseJson.message);
        throw new Error(responseJson.message);
      }

      const { order_id } = responseJson;
      return window.localStorage.setItem("tp_order_id", order_id)
    } else {
      const errorText = await response.text();
      console.error("Error: Expected JSON but received:", errorText);
      throw new Error("Received non-JSON response");
    }
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export const postPassengers = async (
  payload: {
    passengers: {
      full_name: string;
      phone: string;
      email: string;
      date_of_birth: Date | undefined;
      id_type: string;
      id_number: string;
      nationality: string;
      public_id?: string;
    }[],
    bookingQuestionsAnswers: QuestionAnswers
  }, 
  orderId: string
) => {
  try {
    const response = await fetch(
      process.env.REACT_APP_API_URL + "/passengers/experience/" + orderId + "/change",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response text:", errorText);
      throw new Error("Network response was not ok");
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return response.json();
    } else {
      const errorText = await response.text();
      console.error("Error: Expected JSON but received:", errorText);
      throw new Error("Received non-JSON response");
    }
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

type Customer = {
  full_name: string;
  email: string;
  address: string;
  zipcode: string;
  country: string;
  id_type: string;
  id_number: string;
  public_id?: string;
}

export const postCustomer = async (customer: Customer, orderId: string) => {
  try {
    const response = await fetch(
      process.env.REACT_APP_API_URL + "/customers/" + orderId + "/experience/change",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(customer),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response text:", errorText);
      throw new Error("Network response was not ok");
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return response.json();
    } else {
      const errorText = await response.text();
      console.error("Error: Expected JSON but received:", errorText);
      throw new Error("Received non-JSON response");
    }
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

// export const postCreateTransaction = async (paymentId, paymentProvider) => {
//   // We'll eventually load these directly from the server
//   const PAYMENT_PROVIDERS = {
//     transfer: {
//       url: "/offline-payments/create-transaction"
//     },
//     cash: {
//       url: "/offline-payments/create-transaction"
//     },
//     bank: {
//       url: "/offline-payments/create-transaction"
//     }
//   }

//   if (!PAYMENT_PROVIDERS[paymentProvider]) {
//     throw new Error ("Payment provider not found.")
//   }

//   try {
//     const response = await fetch(
//       process.env.REACT_APP_API_URL + PAYMENT_PROVIDERS[paymentProvider].url,
//       {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({ payment_public_id: paymentId, payment_provider_code: paymentProvider}),
//       }
//     );

//     if (!response.ok) {
//       const errorText = await response.text();
//       console.error("Error response text:", errorText);
//       throw new Error("Network response was not ok");
//     }

//     const contentType = response.headers.get("content-type");
//     if (contentType && contentType.includes("application/json")) {
//       return response.json();
//     } else {
//       const errorText = await response.text();
//       console.error("Error: Expected JSON but received:", errorText);
//       throw new Error("Received non-JSON response");
//     }
//   } catch (error) {
//     console.error("Fetch error:", error);
//     throw error;
//   }
// };
